/**
 * hostVolumeControl — regra B2B "volume dono do host" (só embedded).
 * =================================================================
 *
 * Fonte ÚNICA da verdade de QUEM controla o volume do player embedded:
 *
 * - Por padrão o **servidor Efflux** manda (config `volumeLevel` aplicada pelo
 *   {@link ConfigManager}, como em qualquer player da frota).
 * - Assim que o **host** (APK que embute o Efflux) chama `setVolume(n)` pelo
 *   canal embedded, o host ASSUME o controle: o volume vira "sticky" local e o
 *   {@link ConfigManager} para de aplicar o volume do servidor. Só volta pro
 *   servidor quando o host chama `releaseVolume()` (o "vol → server" do demo).
 *
 * Estado persistido em localStorage → sobrevive a `location.reload()` (Fase 9 /
 * comando `reload`) e à recriação do WebView (Fase 9b): o host não precisa
 * re-afirmar o controle depois de um auto-recovery.
 *
 * ⚠️ **Impacto zero nas outras plataformas.** Só o adapter de comandos embedded
 * (`storeCommandHandlers`) escreve aqui. Em tizen/webos/web/android o estado
 * NUNCA é setado → `isHostVolumeControlled()` = false e `hasHostVolumeState()` =
 * false → o ConfigManager e o SyncService se comportam exatamente como antes.
 */

const STORAGE_KEY = 'efflux_host_volume_control_v1';

interface HostVolumeState {
  /** Host está no controle AGORA (setVolume sem releaseVolume depois). */
  controlled: boolean;
  /** Último nível (0-100) que o host aplicou; null quando liberado. */
  level: number | null;
  /**
   * Host JÁ assumiu o controle ao menos uma vez nesta identidade. Marca que a
   * telemetria (Parte B) deve reportar o estado no sync — inclusive o
   * `controlled=false` pós-release, pra o admin limpar o badge. Nunca setado
   * fora do embedded → frota atual não reporta nada novo.
   */
  everControlled: boolean;
}

function clampLevel(n: number): number {
  if (!Number.isFinite(n)) return 0;
  return Math.max(0, Math.min(100, Math.round(n)));
}

function load(): HostVolumeState {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const p = JSON.parse(raw) as Partial<HostVolumeState>;
      return {
        controlled: p.controlled === true,
        level: typeof p.level === 'number' ? clampLevel(p.level) : null,
        everControlled: p.everControlled === true || p.controlled === true,
      };
    }
  } catch {
    /* storage indisponível/corrompido → default seguro (servidor no controle) */
  }
  return { controlled: false, level: null, everControlled: false };
}

let state: HostVolumeState = load();

function persist(): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    /* best-effort: memória segue como verdade nesta sessão */
  }
}

/**
 * Host assumiu o controle do volume no nível informado (0-100). Torna o volume
 * sticky local — o servidor deixa de sobrescrever até `releaseHostVolume()`.
 */
export function markHostVolume(level: number): void {
  state = { controlled: true, level: clampLevel(level), everControlled: true };
  persist();
}

/**
 * Host devolveu o controle ao servidor Efflux. O `everControlled` permanece
 * true → o próximo sync reporta `controlled=false` pra o admin limpar o badge.
 */
export function releaseHostVolume(): void {
  state = {
    controlled: false,
    level: null,
    everControlled: state.everControlled,
  };
  persist();
}

/** true enquanto o host detém o controle do volume (gate do ConfigManager). */
export function isHostVolumeControlled(): boolean {
  return state.controlled;
}

/** Nível (0-100) que o host aplicou, ou null quando o servidor está no controle. */
export function getHostVolumeLevel(): number | null {
  return state.level;
}

/**
 * true se o host já tocou no volume ao menos uma vez → a telemetria do sync deve
 * carregar o estado. false na frota inteira (ninguém escreve fora do embedded).
 */
export function hasHostVolumeState(): boolean {
  return state.everControlled;
}

/** TEST-ONLY — zera memória + storage entre casos. */
export function __resetHostVolumeControlForTests(): void {
  state = { controlled: false, level: null, everControlled: false };
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* ignore */
  }
}
